import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import NavigationIcon from "@mui/icons-material/Navigation";
import SquareRoundedIcon from "@mui/icons-material/SquareRounded";
import Tooltip from "@mui/material/Tooltip";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { DeviceActivity } from "@/utilities/deviceActivity";

/**
 * Whether a vehicle is moving, standing or silent, to sit in front of its name.
 *
 * An arrow for moving and a square otherwise, greyed out where the vehicle is not reporting — so
 * the shape says what it is doing and the colour says whether that is current. The label is both
 * the tooltip and the icon's accessible name, because the shape alone does not name the state.
 * A failed status query gets its own error icon rather than falling into "unknown" — "unknown"
 * reads as "still loading" when it never will.
 */
export function DeviceActivityIcon({ activity }: { activity: DeviceActivity }) {
  const { t } = useTranslation();

  const label = deviceActivityLabel(activity, t);

  if (activity === "error") {
    return (
      <Tooltip title={label}>
        <ErrorOutlineIcon
          color="error"
          fontSize="small"
          titleAccess={label}
          sx={{ flexShrink: 0 }}
        />
      </Tooltip>
    );
  }

  const isReporting = activity === "driving" || activity === "stopped";
  const Icon = activity === "driving" ? NavigationIcon : SquareRoundedIcon;

  return (
    <Tooltip title={label}>
      <Icon
        color={isReporting ? "primary" : "disabled"}
        fontSize="small"
        titleAccess={label}
        sx={{ flexShrink: 0 }}
      />
    </Tooltip>
  );
}

/** The wording for an activity, which is both the tooltip and the accessible name. */
export function deviceActivityLabel(activity: DeviceActivity, t: TFunction): string {
  switch (activity) {
    case "driving":
      return t("deviceStatus.activity.driving");
    case "stopped":
      return t("deviceStatus.activity.stopped");
    case "offline":
      return t("deviceStatus.activity.offline");
    case "error":
      return t("deviceStatus.activity.error");
    default:
      return t("deviceStatus.activity.unknown");
  }
}
